import React, { ReactNode, useContext, useMemo } from 'react'
import { Typography, styledComponent } from '@hermes/web-components'
import { AppDataContext } from '../../../providers/AppData'
import StarIcon from '../../SearchPage/Alternative/QuickFactsAndQuestions/Icons/StarIcon'
import ContactChatIcon from '../../SearchPage/Alternative/QuickFactsAndQuestions/Icons/ContactChatIcon'
import SuccessIcon from '../../SearchPage/Alternative/QuickFactsAndQuestions/Icons/SuccessIcon'
import CreditCardIcon from '../../SearchPage/Alternative/QuickFactsAndQuestions/Icons/CreditCardIcon'
import EndoscopeIcon from '../../SearchPage/Alternative/QuickFactsAndQuestions/Icons/EndoscopeIcon'
import GenderIcon from '../../SearchPage/Alternative/QuickFactsAndQuestions/Icons/GenderIcon'
import { QuickFactsAndQuestions } from '../../../types/quickFacts'
import { Location, ProfileKeyword } from '../../../types'
import useTranslateMessage from 'hooks/useTranslateMessage'
import { ProfileTypes } from '../../../constants/profile'
import { ProfileTypes as ProfileSearchTypes } from '../../../providers/SearchProfilesPage/types'

const LinkText = styledComponent(Typography)<{ component: string; children: ReactNode }>({
  color: '#2C3341',
  fontSize: '15px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '20px',
  textDecorationLine: 'underline',
  textDecorationStyle: 'solid',
  textDecorationThickness: 'auto',
  textUnderlineOffset: 'auto',
  textUnderlinePosition: 'from-font'
})

const useBuildQuickFactsContent = (
  profileKeyword: ProfileKeyword,
  location: Location,
  quickFacts?: QuickFactsAndQuestions | null
) => {
  const { language, locale } = useContext(AppDataContext)
  const translate = useTranslateMessage()

  // eslint-disable-next-line complexity
  return useMemo(() => {
    const facts = []
    const city = location?.name?.[language]

    const {
      topRated,
      topReviewed,
      topEndorsed,
      fees,
      proceduresAverage,
      genderPercentage,
      totalVerified,
      averageRating,
      countResults,
      treatmentsAverage,
      questions = [],
      profileType,
      profileSearchType
    } = quickFacts || {}

    const keyword =
      profileType === ProfileTypes.SPECIALIST
        ? profileKeyword?.practitioner?.[language]
        : profileKeyword?.name?.[language]
    const keywordType = profileKeyword.type
    const isTreatmentKeyword = keywordType === 'condition' || keywordType === 'procedure'
    const isPlainSearchType =
      profileSearchType === ProfileSearchTypes.practices || profileSearchType === ProfileSearchTypes.specialists
    const keywordTypeSuffix = isTreatmentKeyword && isPlainSearchType ? '.treatment' : ''
    const profileBasePath = `/${locale}/${profileType}`

    if (topRated?.slug) {
      facts.push({
        title: translate(`search.quick_facts.${profileSearchType}.top_rated${keywordTypeSuffix}`, {
          keyword,
          location: city
        }),
        value: (
          <a href={`${profileBasePath}/${topRated?.slug}`}>
            <LinkText component="span">{topRated?.name?.[language]}</LinkText>
          </a>
        ),
        icon: <StarIcon style={{ width: '24px', height: '21px' }} />
      })
    }

    if (topReviewed?.slug) {
      facts.push({
        title: translate(`search.quick_facts.${profileSearchType}.top_reviewed${keywordTypeSuffix}`, {
          keyword,
          location: city
        }),
        value: (
          <a href={`${profileBasePath}/${topReviewed?.slug}`}>
            <LinkText component="span">{topReviewed?.name?.[language]}</LinkText>
          </a>
        ),
        icon: <ContactChatIcon style={{ width: '24px', height: '24px', color: '#2C3341' }} />
      })
    }

    if (topEndorsed?.slug) {
      facts.push({
        title: translate(`search.quick_facts.${profileSearchType}.top_endorsed${keywordTypeSuffix}`, {
          keyword,
          location: city
        }),
        value: (
          <a href={`${profileBasePath}/${topEndorsed?.slug}`}>
            <LinkText component="span">{topEndorsed?.name?.[language]}</LinkText>
          </a>
        ),
        icon: <SuccessIcon style={{ width: '24px', height: '21px' }} />
      })
    }

    if (totalVerified && totalVerified !== '0') {
      facts.push({
        title: translate(`search.quick_facts.${profileSearchType}.total_verified${keywordTypeSuffix}`, {
          keyword,
          location: city
        }),
        value: +totalVerified,
        icon: <SuccessIcon style={{ width: '24px', height: '21px' }} />
      })
    }

    if (averageRating && averageRating !== '0') {
      facts.push({
        title: translate(`search.quick_facts.${profileSearchType}.average_rating${keywordTypeSuffix}`, {
          keyword,
          location: city
        }),
        value: (+averageRating).toFixed(2),
        icon: <EndoscopeIcon style={{ width: '24px', height: '21px' }} />
      })
    }

    if (countResults && countResults !== '0') {
      facts.push({
        title: translate(`search.quick_facts.${profileSearchType}.count_results${keywordTypeSuffix}`, {
          keyword,
          location: city
        }),
        value: countResults,
        icon: <CreditCardIcon style={{ width: '24px', height: '22px' }} />
      })
    }

    if (treatmentsAverage && treatmentsAverage !== '0') {
      facts.push({
        title: translate(`search.quick_facts.${profileSearchType}.average_treatments${keywordTypeSuffix}`, {
          keyword,
          location: city
        }),
        value: treatmentsAverage,
        icon: <CreditCardIcon style={{ width: '24px', height: '22px' }} />
      })
    }

    if ((fees?.new || fees?.follow) && fees?.currency) {
      const min = Math.min(+fees.new, +fees.follow)
      const max = Math.max(+fees.new, +fees.follow)
      const value = min === max ? `${fees.currency}${min}` : `Between ${fees.currency}${min} - ${fees.currency}${max}`
      facts.push({
        title: translate(`search.quick_facts.${profileSearchType}.fees${keywordTypeSuffix}`, {
          keyword,
          location: city
        }),
        value,
        icon: <CreditCardIcon style={{ width: '24px', height: '22px' }} />
      })
    }

    if (proceduresAverage && proceduresAverage !== '0') {
      facts.push({
        title: translate(`search.quick_facts.${profileSearchType}.procedures${keywordTypeSuffix}`, {
          keyword,
          location: city
        }),
        value: proceduresAverage,
        icon: <EndoscopeIcon style={{ width: '24px', height: '21px' }} />
      })
    }

    if (genderPercentage?.female || genderPercentage?.male || genderPercentage?.others) {
      facts.push({
        title: translate(`search.quick_facts.${profileSearchType}.gender${keywordTypeSuffix}`, {
          keyword,
          location: city
        }),
        value: `${genderPercentage?.female}% ${translate('female')}, ${genderPercentage?.male}% ${translate('male')}, ${
          genderPercentage?.others
        }% ${translate('other')}`,
        icon: <GenderIcon style={{ width: '24px', height: '30px' }} />
      })
    }

    return {
      QAs: questions,
      QAsTitle: translate(`search.top_questions.${profileSearchType}.title${keywordTypeSuffix}`, {
        keyword,
        location: city
      }),
      facts,
      factsTitle: translate('search.quick_facts.title')
    }
  }, [
    location?.name,
    language,
    quickFacts,
    profileKeyword?.practitioner,
    profileKeyword?.name,
    profileKeyword.type,
    locale,
    translate
  ])
}

export default useBuildQuickFactsContent
